import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { parseISO } from 'date-fns';
import { useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useSession } from 'src/contexts/SessionContext';
import { fetchUserDependents } from 'src/models/user/user';
import { setViewAppearCallback } from 'src/utils/app-bridge';

import { fetchAppointments } from '../../models/appointment/appointment';

import type { FilterType, Params } from './types';
import type { Appointment } from '../../models/appointment/_types';
import type { Identifier } from '../../types/utilityTypes';

const appointmentsPerPage = 10;

export const useAppointmentsPage = () => {
  const queryClient = useQueryClient();
  const params: Params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const [curAppointmentId, setCurAppointmentId] = useState<Identifier | null>(
    null
  );

  const [isScrolled, setIsScrolled] = useState(false);
  const { user } = useSession();

  const filter: FilterType = params.filter || 'upcoming';
  const { allStaff, allDependents, date, f_locationId: locationId } = Object.fromEntries([
    ...searchParams,
  ]);
  const filterDate = date ? parseISO(date) : null;
  const filterLabels = {
    upcoming: 'upcoming',
    history: 'completed',
    canceled: 'canceled',
  };
  const dateLabels = {
    upcoming: 'starting',
    history: 'before',
    canceled: 'before',
  };

  const { data: dependentsData } = useQuery({
    queryKey: ['users', 'dependents', user.id],

    queryFn: () => {
      return fetchUserDependents({ id: user.id });
    },
  });

  const dependents = dependentsData?.dependents;
  const hasDependents = dependents && dependents.length > 0;

  const {
    data: appointmentsData,
    isLoading: appointmentsLoading,
    isFetching: appointmentsFetching,
    error: appointmentsError,
    fetchNextPage: fetchNextAppointments,
    hasNextPage: hasNextAppointments,
  } = useInfiniteQuery({
    queryKey: ['appointments', filter, allStaff, allDependents, date, locationId],
    queryFn: async ({ pageParam }) => {
      let after = '',
        before = '';

      if (filter == 'upcoming') {
        after = date;
      } else {
        before = date;
      }

      const { appointments, total } = await fetchAppointments({
        page: pageParam,
        perPage: appointmentsPerPage,
        view: filter,
        onlyMe: allStaff != 'true' && allDependents != 'true',
        after,
        before,
        locationId,
      });
      return { data: appointments, pageParam, total };
    },
    getNextPageParam: (lastPage) => {
      const nextPage = (lastPage.pageParam || 1) + 1;
      if (lastPage.pageParam * appointmentsPerPage >= lastPage.total) {
        return undefined;
      }
      return nextPage;
    },
    getPreviousPageParam: (lastPage) => {
      if (lastPage.pageParam <= 1) {
        return undefined;
      }
      return (lastPage.pageParam || 2) - 1;
    },
    initialPageParam: 1,
  });

  const flattenedAppointments =
    appointmentsData?.pages.reduce((list, pageItems) => {
      return [...list, ...pageItems.data];
    }, [] as Appointment[]) || [];

  const curAppointment = flattenedAppointments.find(
    (a) => a.id === curAppointmentId
  );

  const handleClearDate = () => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('date', '');
    setSearchParams(newSearchParams);
  };

  const handleScroll = (e) => {
    const target = e.target as HTMLDivElement;
    setIsScrolled(target.scrollTop > 0);
  };

  const handleScrollTop = () => {
    setIsScrolled(false);

    if (scrollerRef.current) {
      scrollerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  };

  const scrollerRef = useRef<HTMLDivElement>(null);

  setViewAppearCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['appointments'] });
  });

  return {
    appointments: flattenedAppointments,
    appointmentsLoading,
    appointmentsFetching,
    appointmentsError,
    fetchNextPage: fetchNextAppointments,
    hasNextAppointments,
    curAppointment,
    setCurAppointmentId,
    filter,
    filterLabels,
    dateLabels,
    filterDate,
    handleClearDate,
    allStaff,
    handleScroll,
    handleScrollTop,
    isScrolled,
    scrollerRef,
    hasDependents,
    dependents,
  };
};
